import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  COLUMN_ORDER_ERRORS,
  ModelDeleteEvent,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";
import { AlgoStrategyLookupService } from "shared/cubiq-services/lookup/model/impl/AlgoStrategyLookupService";
import { BfxBlotterOrdersService } from "src/app/bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxConfigIDRefHelper } from "src/shared/helper-services/bfx-config-id-ref-helper";

import { OrderDescriptionHelper } from "../../shared/cubiq-services/model/orders/OrderDescriptionHelper";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { OrderTransferRecord } from "../../shared/cubiq-services/model/orders/OrderTransferRecord";
import { OrderTransferRecordState } from "../../shared/cubiq-services/model/orders/OrderTransferRecordState";
import { BMRiskUtils } from "shared/cubiq-services/model/benchmarks/helpers/BMRiskUtils";
import { LoggerService } from "@bfx/logger";
import { TransferUtils } from "shared/cubiq-services/model/orders/TransferUtils";
const logger = LoggerService.for(__dirname);

export const STATUS_FAILED: string = "Transfer Rejected";
export const MIN_VISIBLE_ROWS: number = 7;

export const OrderXferTypeMap: any = {
  XFER: OrderTransferRecordState.TRANSFERED,
  XFER_REQUEST: OrderTransferRecordState.ACCEPTANCE,
  XFER_CANCEL: OrderTransferRecordState.ABORT_REJECT,
  XFER_EXPIRE: OrderTransferRecordState.ABORT_REJECT,
  XFER_ACCEPT: OrderTransferRecordState.TRANSFERED,
  XFER_REJECT: OrderTransferRecordState.ABORT_REJECT,
  XFER_STEAL: OrderTransferRecordState.TRANSFERED,
  XFER_STEAL_PENDING: OrderTransferRecordState.ACCEPTANCE,
  XFER_STEAL_REJECT: OrderTransferRecordState.ABORT_REJECT,
};

@Injectable()
export class TransferUtilitiesService {
  private _orderDescriptionHelper: OrderDescriptionHelper;

  constructor(
    private orderBlotterService: BfxBlotterOrdersService,
    tradeGroupService: TraderGroupPromiseLookupService,
    algoStrategyLookupService: AlgoStrategyLookupService,
    sysConfigUtil: BfxSysConfigUtilService,
  ) {
    this._orderDescriptionHelper = new OrderDescriptionHelper(
      tradeGroupService,
      algoStrategyLookupService,
      sysConfigUtil,
    );
  }

  /**
   *
   * Create a new list with all Cross orders replaced by the an order for each component legs  that has
   * the same data as the cross order but with the components allocation instead
   * @param orders
   * @returns
   */
  public processCompositeAllocations(orders: Array<any>): Array<any> {
    let results: Array<any> = [];

    orders.forEach((order: CubiqTS.OrderDataImpl) => {
      // partition allocations into RiskDatas
      const riskMap = BMRiskUtils.createRiskMap(order.allocationData);
      if (riskMap.size == 1) {
        results.push(order);
      } else if (riskMap.size == 3) {
        for (let riskData of riskMap.values()) {
          if (!riskData.isDealt) {
            let orderData = Object.assign(new CubiqTS.OrderDataImpl(), order);
            orderData.allocationData = riskData.allocations;
            results.push(orderData);
          }
        }
      } else {
        logger.out.error(
          `Cannot identify risk for BM order (${riskMap.size} risks found) with ID ${order.orderId}`,
        );
      }
    });
    return results;
  }

  /**
   * For the
   * @param orderIds
   * @param isBenchmark
   * @param withReason
   * @returns
   */
  public createLimitTransferRecords(
    orderIds: Array<CubiqTS.Long> | Array<string>,
    withReason: boolean = true,
  ): Array<OrderTransferRecord> {
    const initOrders: Array<any> = this.orderBlotterService.getRowDataForIdList(orderIds);
    const relatedOrders = this.findRelatedOrders(initOrders);
    let orders: Array<any> = initOrders.concat(relatedOrders);
    let retRecords: Array<OrderTransferRecord> = this.createOrderRecords(orders, false, withReason);
    return retRecords;
  }

  /**
   * Get all related orders to those in the argument  array of orders
   * @param orders
   * @returns Additional related orders that are in the same strategy as the arguments
   */
  protected findRelatedOrders(orders: Array<CubiqTS.OrderData>): Array<CubiqTS.OrderData> {
    return this.orderBlotterService.findRelatedOrders(orders);
  }

  /**
   * For the
   * @param rows Array of OrderDataImpl
   * @param isBenchmark
   * @param withReason
   * @returns
   */
  public createBenchmarkTransferRecords(
    rows: Array<any>,
    withReason: boolean = true,
  ): Array<OrderTransferRecord> {
    let orders: Array<any> = this.processCompositeAllocations(rows);
    let retRecords = this.createOrderRecords(orders, true, withReason);
    return retRecords;
  }

  /**
   * Create order cancel records for the table in this dialog
   * from the adapter row data
   * @param rows raw data rows as adapted in the cubiq services
   * @param withReason should reason be included in the record data
   */
  public createOrderRecords(
    rows: Array<any>,
    isBenchmark: boolean,
    withReason: boolean,
  ): Array<OrderTransferRecord> {
    return rows.map((row: any) => this.createOrderRecord(row, isBenchmark, withReason));
  }

  /**
   * Creates a order cancel record from raw adapter order data records,
   * formulating the order info, trader group name, any reasons if
   * available
   * @param row the raw order data record
   * @param withReason should the reason be included in the record data
   */
  protected createOrderRecord(
    row: any,
    isBenchmark: boolean,
    withReason: boolean,
  ): OrderTransferRecord {
    let orderRecord: OrderTransferRecord = new OrderTransferRecord();
    let order: CubiqTS.OrderDataImpl = row as CubiqTS.OrderDataImpl;

    orderRecord.transferId = this.getTransferId(row, isBenchmark);
    orderRecord.orderId = order.orderId.toString();
    orderRecord.rootOrderId = order?.extendedInfo?.has("rootOrderId")
      ? order.extendedInfo.get("rootOrderId")
      : orderRecord.orderId;
    orderRecord.orderRef = BfxConfigIDRefHelper.getOrderRef(order);
    orderRecord.strategyId = order?.strategyId?.toString() ?? null;
    orderRecord.orderDescription = this._orderDescriptionHelper.createOrderDescription(order);
    orderRecord.orderStatus = "";
    orderRecord.traderGroupName = this._orderDescriptionHelper.createTraderGroupDescription(
      order,
      false,
    );
    orderRecord.rawData = order;

    if (withReason) {
      orderRecord.reason = row[COLUMN_ORDER_ERRORS];
    }

    return orderRecord;
  }

  public getTransferId(order: CubiqTS.OrderDataImpl | ModelDeleteEvent, isBenchmark: boolean) {
    return TransferUtils.getTransferId(order, isBenchmark);
  }
}
